import styles from "./MileageLogsCars.module.scss";
import {MODE_CREATE, MODE_EDIT} from "../../../codes";
import clsx from "clsx";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import useFormat from "../../../util/useFormat";
import {closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors} from "@dnd-kit/core";
import {horizontalListSortingStrategy, SortableContext, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {updatePositions} from "../../../api/ApiCars";
import {restrictToHorizontalAxis, restrictToParentElement} from "@dnd-kit/modifiers";
import {rxReorderCars, rxShowCarEditor, selectAllCars} from "../../../slices/carsSlice";
import {listConfigSelector, rxToggleSelectedCar, selectAllMileageLogs} from "../../../slices/mileageLogsSlice";
import useProject from "../../../util/useProject";
import useNetwork from "../../../util/useNetwork";
import {useTranslation} from "react-i18next";

export const MileageLogsCars = () => {
  const dispatch = useDispatch();
  const cars = useSelector(selectAllCars);

  const {t} = useTranslation("cars");

  const {runWithExpiration} = useProject();
  const {handleNetworkError} = useNetwork();

  const {selectedCarId, inactiveCars} = useSelector(listConfigSelector);

  const showCarEditor = () => dispatch(rxShowCarEditor({mode: MODE_CREATE, car: {}}));

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  );

  const sortCars = event => {
    const {active, over} = event;

    if (active.id !== over.id) {
      const activeCar = cars.find(a => a.id === active.id);
      const overCar = cars.find(a => a.id === over.id);

      const newCars = [];

      if (overCar.position > activeCar.position) {
        for (let car of cars) {
          if (car === activeCar) {
            continue;
          }

          newCars.push(car);

          if (car === overCar) {
            newCars.push(activeCar);
          }
        }
      } else {
        for (let car of cars) {
          if (car === overCar) {
            newCars.push(activeCar);
          }

          if (car !== activeCar) {
            newCars.push(car);
          }
        }
      }

      dispatch(rxReorderCars(newCars));

      updatePositions(newCars.map(a => a.id)).catch(handleNetworkError);
    }
  };

  return <div id={styles.cars}>
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={sortCars}
      modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={cars.filter(c => inactiveCars || c.active)}
        strategy={horizontalListSortingStrategy}
      >
        {cars.filter(c => inactiveCars || c.active).map(c =>
          <SortableCar key={c.id} car={c} selectedCarId={selectedCarId}/>)}
      </SortableContext>
    </DndContext>

    {cars.length > 0 && <button className={clsx(styles.addCar)} onClick={runWithExpiration(showCarEditor)}>
      <span className="material-icons">add</span>
    </button>}

    {cars.length === 0 && <button className={clsx(styles.addCarNoCars)} onClick={runWithExpiration(showCarEditor)}>
      <span className="material-icons">add</span>

      {t("button_add_car")}
    </button>}
  </div>;
};

const SortableCar = ({car, selectedCarId}) => {
  const [background, color] = car.color.split("|");
  const active = selectedCarId === car.id;

  const dispatch = useDispatch();

  const mileageLogs = useSelector(selectAllMileageLogs);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({id: car.id});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const {formatNumber} = useFormat();

  const toggleSelectedCar = () => dispatch(rxToggleSelectedCar({car}));
  const showCarEditor = () => dispatch(rxShowCarEditor({mode: MODE_EDIT, car}));

  let averageConso = null;

  const carMileageLogs = mileageLogs.filter(ml => ml.car === car.id);

  if (carMileageLogs.length > 1) {
    const lastMileageLog = carMileageLogs[0];
    const firstMileageLog = carMileageLogs[carMileageLogs.length - 1];

    const fullQuantity = carMileageLogs.map(ml => ml.quantity).reduce((a, b) => a + b, 0) - firstMileageLog.quantity;
    const fullDistance = lastMileageLog.odometer - firstMileageLog.odometer;

    averageConso = fullQuantity / fullDistance * 100;
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
         className={clsx(styles.carContainer, active ? styles.active : null)}
    >
      <div className={styles.car}>
        <div className={styles.details} style={{background}} onClick={() => toggleSelectedCar()}>
          <div className={styles.name} style={{color}}>{car.name}</div>
          <div className={styles.conso} style={{color}}>{averageConso ? formatNumber(averageConso, "#T##0D000") : "-"} {car.consoUnit}</div>
        </div>
        <div className={styles.actions} style={{background, color}} onClick={() => showCarEditor()}>
          <span className="material-icons" style={{color, opacity: 0.5}}>edit</span>

          {!car.active && <span className="material-icons" style={{color, opacity: 0.5}}>visibility_off</span>}
        </div>
      </div>

      <div className={styles.activeBar} style={{background, color}}/>
    </div>
  );
};
